import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ArrowUpRight, FileWarning, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { ComplaintsDashboard } from '@/core/components/complaints/ComplaintsDashboard';
import { ComplaintsList } from '@/core/components/complaints/ComplaintsList';
import { CreateComplaintDialog } from '@/core/components/complaints/CreateComplaintDialog';
import { ComplaintDetailDialog } from '@/core/components/complaints/ComplaintDetailDialog';
import { fetchSupplierComplaintBridgeClaims } from '@/modules/8d/api/customer-complaint-bridge';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { useNavigate } from 'react-router-dom';

// QUICK AND DIRTY ONE-OFF:
// Show supplier-facing "customer complaints" by bridging to 8D claims for this tenant only.
// TODO(remove-one-off): Remove this when proper cross-tenant complaint sharing is implemented.
const ONE_OFF_SUPPLIER_BRIDGE_TENANT_ID = '9415c952-3c91-4c9b-995b-7cfedbe7145f';

/**
 * Main page for customer complaints management.
 * Shows dashboard with KPIs and a filterable list of complaints.
 */
export const CustomerComplaintsPage = () => {
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const navigate = useNavigate();
  const [newComplaintId, setNewComplaintId] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  // One-off feature flag by tenant id.
  const isOneOffSupplierBridgeTenant = selectedTenantId === ONE_OFF_SUPPLIER_BRIDGE_TENANT_ID;

  const {
    data: bridgedClaims = [],
    isLoading: isLoadingBridgedClaims,
    error: bridgedClaimsError,
  } = useQuery({
    // Separate key so this temporary feed never collides with normal complaints queries.
    queryKey: ['supplier-complaint-bridge-claims', selectedTenantId],
    // Uses edge function bridge because direct table reads are tenant-scoped by RLS.
    queryFn: fetchSupplierComplaintBridgeClaims,
    enabled: isOneOffSupplierBridgeTenant,
  });

  const handleComplaintCreated = (complaintId: string) => {
    setNewComplaintId(complaintId);
    setDetailDialogOpen(true);
  };

  // QUICK AND DIRTY ONE-OFF VIEW:
  // For the supplier tenant, replace the regular complaints UX with 8D-backed rows.
  if (isOneOffSupplierBridgeTenant) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <FileWarning className="h-8 w-8 text-primary" />
            Customer Complaints
          </h1>
          <p className="text-muted-foreground mt-2">
            One-off supplier view: showing linked 8D claims as customer complaints
          </p>
        </div>

        <Alert>
          <AlertDescription>
            Temporary bridge enabled for supplier onboarding. Open any row to continue in the token-based 8D view.
          </AlertDescription>
        </Alert>

        {isLoadingBridgedClaims ? (
          <Card>
            <CardContent className="py-12 flex items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading complaints...
            </CardContent>
          </Card>
        ) : bridgedClaimsError ? (
          <Alert variant="destructive">
            <AlertDescription>
              {bridgedClaimsError instanceof Error ? bridgedClaimsError.message : 'Failed to load bridged complaints.'}
            </AlertDescription>
          </Alert>
        ) : bridgedClaims.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <p>No open supplier claims found.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {bridgedClaims.map((claim) => (
              <Card key={claim.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="text-lg">{claim.title}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1 font-mono">{claim.claim_number}</p>
                    </div>
                    <Badge variant="outline">{claim.status.replace(/_/g, ' ')}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="flex items-center justify-between gap-4">
                  <p className="text-sm text-muted-foreground">
                    Updated {format(new Date(claim.updated_at), 'MMM d, yyyy HH:mm')}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    // Open the existing token-based supplier portal view directly.
                    onClick={() => navigate(`/8d/supplier/${claim.id}?token=${claim.magic_token}`)}
                  >
                    Open
                    <ArrowUpRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Normal customer complaints flow for all non-bridge tenants.
  if (!selectedFactoryId) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <AlertDescription>Please select a factory to view customer complaints.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <FileWarning className="h-8 w-8 text-primary" />
            Customer Complaints
          </h1>
          <p className="text-muted-foreground mt-2">
            Track and manage customer complaints with 8D integration
          </p>
        </div>
        <CreateComplaintDialog onCreated={handleComplaintCreated} />
      </div>

      {/* Tabs: Dashboard vs List */}
      <Tabs defaultValue="dashboard" className="space-y-6">
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="list">All Complaints</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          <ComplaintsDashboard />
          
          {/* Recent Complaints Preview */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Recent Complaints</h2>
            <ComplaintsList />
          </div>
        </TabsContent>

        <TabsContent value="list">
          <ComplaintsList />
        </TabsContent>
      </Tabs>

      {/* Detail dialog for newly created complaints */}
      <ComplaintDetailDialog
        complaintId={newComplaintId}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
      />
    </div>
  );
};
